// src/pages/calculator/DoseCalculator.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicineAPI, dosageAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Pill, LayoutDashboard, Check, X, AlertTriangle, Calculator,
  ClipboardList, Link as LinkIcon, ArrowUpRight, Info, AlertOctagon, Loader2
} from 'lucide-react';
import '../../styles/pages.css';

const SEVERITY_CLASS = {
  low: 'alert-info',
  medium: 'alert-warning',
  info: 'alert-info',
  warning: 'alert-warning',
  high: 'alert-danger',
  danger: 'alert-danger',
  critical: 'alert-critical',
};

const SEVERITY_ICON = {
  low: <Info size={18} />,
  medium: <AlertTriangle size={18} />,
  info: <Info size={18} />,
  warning: <AlertTriangle size={18} />,
  high: <AlertTriangle size={18} />,
  danger: <AlertTriangle size={18} />,
  critical: <AlertOctagon size={18} />,
};

export default function DoseCalculator() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [selected, setSelected] = useState(null);
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveLog, setSaveLog] = useState(false);

  const dropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Search medicines with debounce
  useEffect(() => {
    if (!search || selected) { setMedicines([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await medicineAPI.getAll({ search });
        setMedicines(res.data.data.slice(0, 8));
        setShowDrop(true);
      } catch { }
    }, 300);
    return () => clearTimeout(t);
  }, [search, selected]);

  const selectMedicine = (med) => {
    setSelected(med);
    setSearch(med.name);
    setShowDrop(false);
    setResult(null);
    setError('');
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!selected) { setError('Please select a medicine'); return; }
    if (!age || !weight) { setError('Please enter age and weight'); return; }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await dosageAPI.calculate({
        medicine_id: selected.id,
        age: parseFloat(age),
        weight: parseFloat(weight),
        save_log: saveLog,
      });
      setResult(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Calculation failed. No dosage rule found for these parameters.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelected(null);
    setSearch('');
    setAge('');
    setWeight('');
    setResult(null);
    setError('');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)' }}>
      {/* Top bar */}
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--gray-100)',
        padding: '0 28px', height: 60, display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, background: 'var(--primary)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
            <Pill size={18} />
          </div>
          <span style={{ fontWeight: 700, fontSize: '1.0625rem', letterSpacing: '-0.02em' }}>MediDose</span>
        </div>
        <div style={{ flex: 1 }} />
        {user?.roles?.includes('admin') && (
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin')}>
            <LayoutDashboard size={16} style={{ marginRight: 6 }} /> Admin Panel
          </button>
        )}
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/calculators/medical')}>
          <Calculator size={16} style={{ marginRight: 6 }} /> Medical Calculators</button>

        <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>{user?.name}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => { logout(); navigate('/login'); }}>Sign out</button>
      </header>

      <div className="calc-page">
        {/* Hero */}
        <div className="calc-hero">
          <h1>Dose Calculator</h1>
          <p>Weight-based medication dosing with clinical safety checks</p>
        </div>

        {/* Calculator Form */}
        <div className="card calc-card">
          <div className="card-body">
            <form onSubmit={handleCalculate}>
              {/* Medicine Search */}
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label required">Medicine</label>
                <div className="medicine-search-wrap" ref={dropRef}>
                  <input
                    className="form-input"
                    placeholder="Search medicine name…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setSelected(null); setResult(null); }}
                    onFocus={() => medicines.length && setShowDrop(true)}
                    autoComplete="off"
                  />
                  {showDrop && medicines.length > 0 && (
                    <div className="medicine-dropdown">
                      {medicines.map(m => (
                        <div key={m.id} className="medicine-option" onMouseDown={() => selectMedicine(m)}>
                          <div>{m.name}</div>
                          <div className="medicine-option-sub">{m.generic_name || m.category_name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selected && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="badge badge-primary" style={{ display: 'flex', alignItems: 'center' }}>
                      <Check size={14} style={{ marginRight: 4 }} /> {selected.name}
                    </span>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center' }} onClick={handleReset}>
                      <X size={14} style={{ marginRight: 4 }} /> Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Patient Parameters */}
              <div className="calc-section-title">Patient Parameters</div>
              <div className="calc-grid" style={{ marginBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label required">Age</label>
                  <div className="calc-input-group">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      max="120"
                      step="0.1"
                      placeholder="e.g. 5.5"
                      value={age}
                      onChange={e => setAge(e.target.value)}
                      style={{ paddingRight: 48 }}
                    />
                    <span className="calc-input-unit">yrs</span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label required">Weight</label>
                  <div className="calc-input-group">
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="e.g. 20"
                      value={weight}
                      onChange={e => setWeight(e.target.value)}
                      style={{ paddingRight: 48 }}
                    />
                    <span className="calc-input-unit">kg</span>
                  </div>
                </div>
              </div>

              {/* Save log option */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 20, cursor: 'pointer' }}>
                <input type="checkbox" checked={saveLog} onChange={e => setSaveLog(e.target.checked)} />
                Save this calculation to audit log
              </label>

              {error && (
                <div className="alert alert-danger" style={{ marginBottom: 16 }}>
                  <AlertTriangle size={18} style={{ marginRight: 6 }} /> {error}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {loading ? <><Loader2 className="spinner" size={20} style={{ marginRight: 8 }} /> Calculating…</> : <><Calculator size={20} style={{ marginRight: 8 }} /> Calculate Dose</>}
              </button>
            </form>
          </div>
        </div>

        {/* Result Panel */}
        {result && (
          <>
            <div className="result-panel">
              <div className="result-headline" style={{ display: 'flex', alignItems: 'center' }}>
                <ClipboardList size={22} style={{ marginRight: 8 }} /> Calculation Result — {result.medicine}
              </div>

              <div className="result-dose">
                <span className="result-dose-value">{result.calculated_dose}</span>
                <span className="result-dose-unit">{result.unit}</span>
              </div>
              <div className="result-frequency">
                {result.frequency ? `${result.frequency}` : 'Frequency not specified'}
                {result.frequency_per_day ? ` (${result.frequency_per_day}× per day)` : ''}
              </div>

              <div className="result-meta-grid">
                <div className="result-meta-item">
                  <div className="result-meta-label">Dose per kg</div>
                  <div className="result-meta-value">{result.dose_per_kg} {result.unit}/kg</div>
                </div>
                <div className="result-meta-item">
                  <div className="result-meta-label">Max Dose</div>
                  <div className="result-meta-value">{result.max_dose ? `${result.max_dose} ${result.unit}` : '—'}</div>
                </div>
                <div className="result-meta-item">
                  <div className="result-meta-label">Daily Total</div>
                  <div className="result-meta-value">{result.daily_dose ? `${result.daily_dose} ${result.unit}` : '—'}</div>
                </div>
              </div>

              {/* Exceeded warning banner */}
              {result.exceeded && (
                <div className="alert alert-warning" style={{ marginTop: 16 }}>
                  <AlertTriangle size={20} style={{ marginRight: 8 }} />
                  <div>Calculated dose exceeded the maximum safe limit and has been capped. Review with a pharmacist.</div>
                </div>
              )}
            </div>

            {/* Warnings */}
            {result.warnings && result.warnings.length > 0 && (
              <div className="card calc-card">
                <div className="card-header" style={{ display: 'flex', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, fontSize: '0.9375rem', display: 'flex', alignItems: 'center' }}>
                    <AlertTriangle size={18} style={{ marginRight: 6 }} /> Safety Warnings & Alerts
                  </h4>
                  <span className="badge badge-warning">{result.warnings.length}</span>
                </div>
                <div className="card-body">
                  <div className="warnings-list">
                    {result.warnings.map((w, i) => (
                      <div key={i} className={`alert ${SEVERITY_CLASS[w.type] || 'alert-info'}`}>
                        <span>{SEVERITY_ICON[w.type] || <Info size={18} />}</span>
                        <div>{w.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Drug Interactions */}
            {result.interactions && result.interactions.length > 0 && (
              <div className="card calc-card">
                <div className="card-header" style={{ display: 'flex', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, fontSize: '0.9375rem', display: 'flex', alignItems: 'center' }}>
                    <LinkIcon size={18} style={{ marginRight: 6 }} /> Drug Interactions
                  </h4>
                  <span className="badge badge-danger">{result.interactions.length}</span>
                </div>
                <div className="card-body">
                  {result.interactions.map((ix, i) => (
                    <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-100)' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{ix.medicine_a_name} + {ix.medicine_b_name}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>{ix.description}</div>
                      {ix.recommendation && <div style={{ fontSize: '0.875rem', color: 'var(--primary-dark)', marginTop: 4, display: 'flex', alignItems: 'center' }}>
                        <ArrowUpRight size={14} style={{ marginRight: 4 }} /> {ix.recommendation}
                      </div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={handleReset}>Calculate Another Dose</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
